import { useState, useEffect, useRef } from 'react';
import { User, UserRole, Permission, ROLE_PERMISSIONS, Organization, OrgStatus, PlanFeature, ModuleId, MASTER_ORG_ID, isPlatformRole, isOwnerRole, SubscriptionPlan, UserStatus } from '../domain/entities';
import { getRepository, resetRepository } from '../config/repository';
import { setAuthToken, getAuthToken } from '../data/ApiModuleRepository';
import { hasPermission as _hasPermission, filterByScope, canAccessModule as _canAccessModule } from '../core/security';
import { canUsePlanFeature as _canUsePlanFeature } from '../core/planGating';

/** Always returns the current repo singleton (picks up resets). */
const repo = () => getRepository();

const SIGNED_OUT_KEY = 'dm_signed_out';
const TOKEN_REFRESH_INTERVAL = 20 * 60 * 1000; // 20 minutes

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [demoUsers, setDemoUsers] = useState<User[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = async () => {
    setLoading(true);

    // If signed out, stay signed out
    if (localStorage.getItem(SIGNED_OUT_KEY) === '1') {
      setUser(null);
      setCurrentOrg(null);
      setLoading(false);
      return;
    }

    // No stored JWT token — treat as signed out
    if (!getAuthToken()) {
      setUser(null);
      setCurrentOrg(null);
      setLoading(false);
      return;
    }

    let currentUser: User | null = null;
    try {
      const [cu, plans] = await Promise.all([
        repo().getCurrentUser(),
        repo().getSubscriptionPlans(),
      ]);
      currentUser = cu;
      setSubscriptionPlans(plans);

      if (_hasPermission(cu, 'sys:manage_users' as Permission)) {
        try {
          setDemoUsers(await repo().getUsers());
        } catch {
          setDemoUsers([cu]);
        }
      } else {
        setDemoUsers([cu]);
      }
    } catch (err: any) {
      // Stale/invalid token — clear auth and go to login
      if (err?.status === 401 || err?.status === 403) {
        setAuthToken(null);
        setUser(null);
        setCurrentOrg(null);
        setDemoUsers([]);
        setLoading(false);
        return;
      }
      throw err;
    }

    if (currentUser && !isPlatformRole(currentUser.role) && ![undefined, UserStatus.ACTIVE].includes(currentUser.status)) {
      setUser(null);
      setCurrentOrg(null);
      setLoading(false);
      return;
    }

    setUser(currentUser);

    // Load org for non-platform users
    const orgId = await repo().getCurrentOrgId();
    if (orgId && orgId !== '__platform__') {
      const r = repo();
      const org = 'getMyOrganization' in r
        ? await (r as any).getMyOrganization()
        : await r.getOrganizationById(orgId);
      setCurrentOrg(org);

      // Block login for any org that is not currently active (platform staff are exempt)
      if (org && !isPlatformRole(currentUser?.role) && ![OrgStatus.ACTIVE, OrgStatus.TRIAL].includes(org.status)) {
        setUser(null);
        setCurrentOrg(null);
        setLoading(false);
        return;
      }
    } else {
      setCurrentOrg(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh JWT token periodically
  useEffect(() => {
    if (!user) return;
    const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
    const doRefresh = async () => {
      const token = getAuthToken();
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json() as { token: string };
          setAuthToken(data.token);
        } else if (res.status === 401 || res.status === 403) {
          // Token invalid/expired — force sign out
          setAuthToken(null);
          localStorage.setItem(SIGNED_OUT_KEY, '1');
          setUser(null);
          setCurrentOrg(null);
        }
      } catch { /* network error — silently skip refresh */ }
    };
    refreshTimerRef.current = setInterval(doRefresh, TOKEN_REFRESH_INTERVAL);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [user]);

  const switchUser = async (newUser: User) => {
    localStorage.removeItem(SIGNED_OUT_KEY);
    await repo().setCurrentUser(newUser);
    // Automatically align org context with the new user's org
    await repo().setCurrentOrgId(newUser.orgId ?? null);
    setUser(newUser);
    await refresh();
  };

  const signOut = async () => {
    localStorage.setItem(SIGNED_OUT_KEY, '1');
    localStorage.removeItem('dm_current_user');
    setAuthToken(null);
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    resetRepository();
    // Hard navigation flushes all JS memory — guarantees clean state for next user
    window.location.href = '/login';
  };

  /**
   * Super Admin only: switches the active org context so all subsequent
   * repository reads return data for that org.
   */
  const switchOrgContext = async (orgId: string | null) => {
    await repo().setCurrentOrgId(orgId);
    await refresh();
  };

  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const isPlatformOwner = isOwnerRole(user?.role);
  const isPlatformSupport = user?.role === UserRole.PLATFORM_SUPPORT;
  const isPlatformSales = user?.role === UserRole.PLATFORM_SALES;
  /** True for ANY internal platform staff (Owner + Support + Sales + legacy SA). */
  const isInternalAdmin = isPlatformRole(user?.role);
  const isAdmin = user?.role === UserRole.ADMIN;
  const isDepartmentHead = user?.role === UserRole.DEPARTMENT_HEAD;
  const isManager = user?.role === UserRole.MANAGER;
  const isEmployee = user?.role === UserRole.EMPLOYEE;
  const isStoreManager = isManager; // backward compat alias
  /** True when platform staff is operating inside the Master Workspace sandbox */
  const isSandboxMode = isInternalAdmin && currentOrg?.id === MASTER_ORG_ID;
  /** True when the user's org has been suspended by the platform */
  const isOrgSuspended = currentOrg?.status === OrgStatus.SUSPENDED;
  /** True when the user should be in read-only mode (Employee role) */
  const isReadOnly = isEmployee;

  const hasPermission = (permission: Permission) => _hasPermission(user, permission);
  const permissions: Permission[] = user ? (ROLE_PERMISSIONS[user.role] ?? []) : [];

  const canUsePlanFeature = (feature: PlanFeature): boolean =>
    _canUsePlanFeature(currentOrg, feature, isInternalAdmin, subscriptionPlans);

  /** Checks BOTH the Org License (enabledApps) AND the User Permission.
   *  Returns false if the org hasn't installed the module, even if the user has the checkbox. */
  const isModuleEnabled = (moduleId: ModuleId): boolean => {
    return _canAccessModule(user, moduleId, undefined, currentOrg?.enabledApps ?? undefined);
  };

  return {
    user,
    demoUsers,
    loading,
    switchUser,
    signOut,
    isSuperAdmin,
    isPlatformOwner,
    isPlatformSupport,
    isPlatformSales,
    isInternalAdmin,
    isAdmin,
    isDepartmentHead,
    isManager,
    isEmployee,
    isStoreManager,
    isReadOnly,
    isSandboxMode,
    isOrgSuspended,
    refresh,
    hasPermission,
    permissions,
    filterByScope,
    currentOrg,
    orgPlan: currentOrg?.plan ?? null,
    canUsePlanFeature,
    isModuleEnabled,
    switchOrgContext,
    subscriptionPlans,
    repository: repo(),
  };
}
